/**
 * notifications-item.js
 */

import React from 'react';
import JsxParser from 'react-jsx-parser';
import { Link as RouterLink, useHistory } from "react-router-dom";
import { Box, Button, Chip, Link, ListItem, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CheckIcon from '@mui/icons-material/Check';
import DateDistance from 'components/date-distance';
import { NOTIFICATION_ANIMATION_DURATION } from "utils/constants";


const useStyles = makeStyles(theme => ({
  item: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    backgroundColor: theme.palette.sys.surfaceContainerHigh,
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(3),
    overflow: 'hidden',
    transition: NOTIFICATION_ANIMATION_DURATION + 'ms cubic-bezier(0.310, 0.000, 0.335, 1.000)',
  },
  itemRead: {
    opacity: 0.8,
    backgroundColor: theme.palette.sys.surfaceContainer
  },
  top: {
    display: 'flex',
    width: '100%',
  },
  date: {
    fontSize: theme.typography.sizes[2],
    marginLeft: theme.spacing(2),
    color: theme.palette.grey.colors[5],
  },
  markReadIcon: {
    height: theme.spacing(4),
    transition: `100ms ${theme.transitions.easing.easeOut}`,
  },
  markReadIconSmall: {
    height: 25,
    width: 25,
    minHeight: 25,
  },
  markReadMarked: {
    display: 'flex',
    marginTop: theme.spacing(.75),
  },
  markReadIconMarked: {
    marginRight: theme.spacing(.5),
  },
  title: {
    color: theme.palette.sys.primary,
    fontWeight: 600,
    marginBottom: theme.spacing(.25),
  },
  message: {
    whiteSpace: 'normal',
    maxWidth: '100%',
    fontSize: theme.typography.sizes[2],
    color: theme.palette.sys.onSurface,
    '& img': {
      width: '100%'
    }
  },
  link: {
    fontWeight: 600,
  },
  chip: {
    color: theme.palette.comp.notifications.chipText,
  },
  hoverPointer: {
    cursor: 'pointer',
  },
  System: {
    backgroundColor: theme.palette.success.main,
  },
  Invitation: {
    backgroundColor: theme.palette.secondary.main,
  },
  Billing: {
    backgroundColor: theme.palette.info.main
  }
}));


export const NotificationItemChip = (props) => {
  const {
    notification,
  } = props;
  const classes = useStyles(props);

  return <Chip size="small" label={notification.type} className={`${classes.chip} ${classes[notification.type]}`} />;
};


export const NotificationItemParseJsxStringAndGenWithLinks = (props) => {
  const classes = useStyles(props);
  const {
    jsxString,
    linkClass = classes.link,
    linkColor,
    linkColorClass = 'MuiTypography-colorPrimary',
    doRemoveImgs = false,
    onLink,
  } = props;
  const history = useHistory();  
  const firstLink = React.useRef();
  const allowedComponents = { Link };
  const linkClasses = `${linkColorClass} ${linkClass}`;


  // Callbacks when clicking links may not work if the link triggers a full page load
  // because async operations like API calls will be interrupted, and other changes e.g. to 
  // state will be irrelevant. Link callbacks can be run if...
  // - it's a download link or opens in another tab/window, leaving the current tab intact, or
  // - it's an internal link that we can handle with the router instead of a full page load
  const handleLink = (e) => {
    const hrefURL = new URL(e.target.href);
    const currentURL = new URL(window.location.href);
    const isInternal = hrefURL.origin === currentURL.origin;
    const bypassesPageLoad = e.target.getAttribute('target') === '_blank' || e.target.getAttribute('download') !== null;

    e.stopPropagation();

    if (isInternal || bypassesPageLoad) {
      onLink && onLink()

      if(!bypassesPageLoad){
        e.preventDefault();
        history.push(`${hrefURL.pathname}${hrefURL.search}${hrefURL.hash}`);
      }
    }
  };

  const handleContainerClick = (e) => {
    firstLink.current && firstLink.current.click();
  };

  const processJsxString = (jsx, doRemoveImgs) => {
    jsx = linkify(jsx);

    if (doRemoveImgs)
      jsx = removeImgs(jsx);

    return jsx;
  };

  const linkify = jsx => {
    let linkIdx = 0;
    return jsx.replace(/<a/g, () => `<Link 
      ${linkIdx++ === 0 ? 'ref={firstLink}' : '' } 
      onClick={handleLink} 
      class="${linkClasses}" 
      ${linkColor ? 'style="color: ' + linkColor + ';"' : ''}
    `).replace(/<\/a>/g, `</Link>`);
  };

  const removeImgs = jsx => {
    return jsx.replace(/<img(.+?)\/>/g, '');
  };

  const parsedJsx = <JsxParser
    blacklistedAttrs={[]}
    components={allowedComponents}
    bindings={{
      RouterLink,
      closeDrawer: props.closeDrawer,
      firstLink,
      handleLink,
    }}
    jsx={processJsxString(jsxString, doRemoveImgs)}
    renderInWrapper={false}
  />

  return (
    <span onClick={handleContainerClick} className={`${firstLink.current ? classes.hoverPointer : ''}`}>
      {parsedJsx}
    </span>
  );
};


export default function NotificationItem(props) {
  const classes = useStyles(props);

  return (
    <ListItem
      key={props.notification.id}
      className={`${classes.item} ${props.notification.isRead ? classes.itemRead : ''}`}
    >
      <div className={classes.top}>
        <Typography variant="h6" display="block" className={classes.title}>
          {props.notification.title}
        </Typography>
        <span style={{ flexGrow: 1 }}></span>
        {!props.notification.isRead && <Button
          color='primary'
          variant='contained'
          title='Dismiss'
          aria-label='Dismiss'
          classes={{
            root: classes.markReadIcon,
            sizeSmall: classes.markReadIconSmall
          }}
          onClick={() => props.markRead(props.notification)}
        >
          Dismiss
        </Button>}
        {props.notification.isRead && <div
          className={classes.markReadMarked}
        >
          <CheckIcon 
            fontSize="small"
            className={classes.markReadIconMarked}
          />
          Dismissed
        </div>}

      </div>
      <Box display='flex' mb={1.5}>
        <NotificationItemChip notification={props.notification} />
        <DateDistance
          dateStart={props.notification.date}
          suffix="ago"
          showTime={true}
          component={Typography}
          componentProps={{ display: 'block', className: classes.date }}
        />
      </Box>
      <Typography
        variant="body1"
        display="block"
        className={classes.message}
      >
        <NotificationItemParseJsxStringAndGenWithLinks 
          jsxString={props.notification.message}
          onLink={() => {props.onLink(); props.markRead(props.notification);}}
        />
      </Typography>
    </ListItem>
  );
}